import { Alert, Box, Container, Loader, Paper, Stack } from '@mantine/core';
import { Navigate, useParams } from 'react-router-dom';
import { useContext } from 'react';
import { useChurchEntitlement } from '../hooks/useChurchEntitlement';
import { buildRoute, PageRoutes } from '../routes';
import { useUserAdmin } from '../hooks/useUserAdmin';
import { AuthContext } from '../context/authContext';
import { useChurch } from '../hooks/useChurch';

export function ChurchAccessBoundary({ children }: { children: React.ReactNode }) {
  const { churchId } = useParams<string>();
  const { user, initialAuthChecked } = useContext(AuthContext);
  const { isAdmin: isPlatformAdmin, loading: loadingPlatformAdmin } = useUserAdmin();
  const { hasAccess, loading, error } = useChurchEntitlement(churchId);
  const { church, loading: loadingChurch } = useChurch(churchId);

  if (!initialAuthChecked) {
    return <Box mih='70vh' style={{ display: 'grid', placeItems: 'center' }}><Loader aria-label='Restoring your session' /></Box>;
  }
  if (!user || user.isAnonymous) return <Navigate to={PageRoutes.LOGIN} replace />;
  if (!churchId) return <Navigate to={PageRoutes.DASHBOARD} replace />;
  if (loading || loadingPlatformAdmin || loadingChurch) {
    return (
      <Box mih='70vh' style={{ display: 'grid', placeItems: 'center' }}>
        <Loader aria-label='Checking church access' />
      </Box>
    );
  }
  if (error) {
    return (
      <Container py='xl'>
        <Paper withBorder p='xl'>
          <Stack><Alert color='red' title='Could not verify access'>{error}</Alert></Stack>
        </Paper>
      </Container>
    );
  }
  if (!isPlatformAdmin && church?.accountStatus === 'pending_review') {
    return <Navigate to={buildRoute(PageRoutes.CHURCH_PENDING_REVIEW, { churchId })} replace />;
  }
  if (!hasAccess && !isPlatformAdmin) {
    return <Navigate to={buildRoute(PageRoutes.SUBSCRIBE, { churchId })} replace />;
  }
  return <>{children}</>;
}
